import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Award,
  BookOpen,
  BriefcaseBusiness,
  CalendarCheck2,
  CheckCircle2,
  Circle,
  ClipboardCheck,
  Compass,
  GraduationCap,
  Loader2,
  MapPinned,
  RefreshCw,
  Sparkles,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";

import { useAppSelector } from "../hooks/reducer";
import { baseUrl } from "../store/slices/auth";

type QuestTaskProgress = {
  task: number;
  activityType?: "quiz" | "game" | "other";
  activityId?: string;
  isDone: boolean;
  result?: unknown;
  doneAt?: string | null;
};

type QuestDayProgress = {
  day: number;
  tasks: QuestTaskProgress[];
  isCompleted: boolean;
  completedAt?: string | null;
};

type QuestWeekProgress = {
  week: number;
  center?: number;
  startedAt?: string;
  finishedAt?: string | null;
  days: QuestDayProgress[];
};

type QuestProgressResponse = {
  progress: {
    weeksCount: number;
    doneTasks: number;
    totalTasks: number;
    overallPercent: number;
    currentWeekNumber: number | null;
    currentWeekPercent: number;
  };
  user: {
    questWeeks?: QuestWeekProgress[];
  };
};

type RoadmapStep = {
  week: number;
  label: string;
  title: string;
  subtitle: string;
  result: string;
  items: string[];
  icon: typeof Target;
};

const cn = (...values: Array<string | false | null | undefined>) =>
  values.filter(Boolean).join(" ");

const roadmap: RoadmapStep[] = [
  {
    week: 1,
    label: "Тиждень 1",
    title: "Я і моє майбутнє",
    subtitle: "Визначаємо стартову точку, цілі та особистий маршрут.",
    result: "Ти розумієш, чого хочеш і куди рухаєшся далі.",
    icon: Compass,
    items: [
      "Психологічний тренінг",
      "Колесо життя",
      "Постановка цілей",
      "Перші кроки особистого плану",
    ],
  },
  {
    week: 2,
    label: "Тиждень 2",
    title: "Кар'єра та гроші",
    subtitle: "Готуємо резюме, співбесіди та кар'єрну карту.",
    result: "Ти маєш кар'єрний план і бачиш реальні вакансії.",
    icon: BriefcaseBusiness,
    items: [
      "Зустрічі з роботодавцями",
      "Резюме та співбесіди",
      "Особисті фінанси",
      "Старт оплачуваної практики",
    ],
  },
  {
    week: 3,
    label: "Тиждень 3",
    title: "Мої можливості",
    subtitle: "Прокачуємо цифрові інструменти для роботи й розвитку.",
    result: "Ти впевненіше користуєшся сучасними робочими сервісами.",
    icon: GraduationCap,
    items: [
      "ChatGPT",
      "Canva",
      "Google Workspace",
      "LinkedIn",
    ],
  },
  {
    week: 4,
    label: "Тиждень 4",
    title: "Я можу впливати",
    subtitle: "Збираємо досвід у портфоліо й плануємо розвиток на рік.",
    result: "Ти виходиш із програмою дій на 12 місяців.",
    icon: Award,
    items: [
      "Комунікація",
      "Лідерство",
      "Соціальні проєкти",
      "План розвитку на 12 місяців",
    ],
  },
];

const centerNames: Record<number, string> = {
  1: "Кар'єра",
  2: "Цифрові навички",
  3: "Особистий розвиток",
};

const clampPercent = (value: number) =>
  Math.max(0, Math.min(100, Math.round(value)));

const getWeekStats = (week?: QuestWeekProgress) => {
  if (!week) return { done: 0, total: 14, percent: 0, completedDays: 0 };

  const days = week.days ?? [];
  const total = days.reduce((sum, day) => sum + (day.tasks?.length || 0), 0) || 14;
  const done = days.reduce(
    (sum, day) => sum + (day.tasks ?? []).filter((task) => task.isDone).length,
    0
  );
  const completedDays = days.filter((day) => day.isCompleted).length;

  return {
    done,
    total,
    percent: clampPercent((done / total) * 100),
    completedDays,
  };
};

const CircularProgress = ({ value }: { value: number }) => {
  const radius = 58;
  const stroke = 11;
  const normalizedRadius = radius - stroke / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const offset = circumference - (clampPercent(value) / 100) * circumference;

  return (
    <div className="relative grid h-[148px] w-[148px] place-items-center">
      <svg className="-rotate-90" height={radius * 2} width={radius * 2}>
        <circle
          stroke="#F1E7B8"
          fill="transparent"
          strokeWidth={stroke}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        <circle
          stroke="#ffd400"
          fill="transparent"
          strokeLinecap="round"
          strokeWidth={stroke}
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={offset}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
      </svg>
      <div className="absolute text-center">
        <p className="text-[34px] font-black leading-none text-black">{clampPercent(value)}%</p>
        <p className="mt-1 text-[11px] font-black uppercase tracking-[0.12em] text-[#8a6f00]">
          успіх
        </p>
      </div>
    </div>
  );
};

const YellowQuestCabinetPage = () => {
  const user = useAppSelector((state) => state.authReducer.data?.user);
  const [progressData, setProgressData] = useState<QuestProgressResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : "";

  const loadProgress = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await axios.get<QuestProgressResponse>(`${baseUrl}quest/progress`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setProgressData(response.data);
    } catch (requestError: any) {
      setError(
        requestError.response?.data?.message ||
          "Не вдалося завантажити прогрес. Спробуйте оновити сторінку."
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  const questWeeks = progressData?.user.questWeeks ?? [];
  const backendProgress = progressData?.progress;

  const activeCourseAverage = useMemo(() => {
    const courses = user?.myActiveCourse ?? [];
    if (courses.length === 0) return 0;
    return clampPercent(
      courses.reduce((sum, course) => sum + (Number(course.progress) || 0), 0) /
        courses.length
    );
  }, [user?.myActiveCourse]);

  const overallPercent = backendProgress
    ? backendProgress.overallPercent
    : activeCourseAverage;

  const currentStep =
    roadmap.find((step) => step.week === backendProgress?.currentWeekNumber) ??
    roadmap.find((step) => getWeekStats(questWeeks[step.week - 1]).percent < 100) ??
    roadmap[roadmap.length - 1];

  const totalCompletedDays = questWeeks.reduce(
    (sum, week) => sum + getWeekStats(week).completedDays,
    0
  );

  const displayName = user?.username || "Учаснику";
  const selectedCenter = user?.center ? centerNames[user.center] : "індивідуальний маршрут";

  return (
    <main className="min-h-[calc(100dvh-5rem)] bg-[#f6f7fb] px-3 py-4 sm:px-5 lg:px-8 lg:py-8">
      <section className="mx-auto max-w-[1240px]">
        <div className="overflow-hidden rounded-[28px] border-2 border-black bg-[#101010] text-white shadow-[6px_6px_0_#ffd400]">
          <div className="relative grid gap-6 p-5 sm:p-7 lg:grid-cols-[1.35fr_0.65fr] lg:p-9">
            <div className="absolute right-0 top-0 hidden h-full w-[38%] opacity-30 lg:block">
              <div className="h-full w-full bg-[repeating-linear-gradient(135deg,rgba(255,212,0,0.42)_0,rgba(255,212,0,0.42)_2px,transparent_2px,transparent_16px)]" />
            </div>

            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#ffd400] px-4 py-2 text-[12px] font-black uppercase tracking-[0.12em] text-black">
                <MapPinned size={15} />
                Мій roadmap
              </div>
              <h2 className="mt-5 max-w-[760px] text-[38px] font-black uppercase leading-[0.95] tracking-[-0.04em] sm:text-[54px] lg:text-[66px]">
                Персональний маршрут розвитку
              </h2>
              <p className="mt-5 max-w-[720px] text-[16px] font-bold leading-7 text-white/78 sm:text-[18px]">
                {displayName}, тут видно твій шлях у програмі: що вже пройдено,
                який тиждень активний зараз і які кроки ведуть до роботи,
                нових навичок та плану розвитку.
              </p>
            </div>

            <div className="relative z-10 flex flex-col justify-between rounded-[24px] border border-white/15 bg-white/8 p-5 backdrop-blur">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#ffd400]">
                    Поточний фокус
                  </p>
                  <h3 className="mt-2 text-2xl font-black">{currentStep.title}</h3>
                </div>
                <Sparkles className="shrink-0 text-[#ffd400]" size={28} />
              </div>
              <p className="mt-4 text-sm font-semibold leading-6 text-white/72">
                {currentStep.subtitle}
              </p>
              <button
                type="button"
                onClick={loadProgress}
                disabled={loading}
                className="mt-6 inline-flex h-12 items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-4 text-sm font-black text-black shadow-[3px_3px_0_#000] transition active:translate-y-0.5 active:shadow-[1px_1px_0_#000] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
                Оновити прогрес
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-5 rounded-2xl border-2 border-[#F16126] bg-white px-4 py-3 text-sm font-bold text-[#8b1e1e]">
            {error}
          </div>
        )}

        <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-[24px] border-2 border-black bg-white p-5 shadow-[4px_4px_0_#ffd400]">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#8a6f00]">
                  Загальний успіх
                </p>
                <p className="mt-2 text-4xl font-black text-black">{overallPercent}%</p>
              </div>
              <TrendingUp className="text-[#ffd400]" size={36} />
            </div>
          </div>

          <div className="rounded-[24px] border-2 border-black bg-white p-5 shadow-[4px_4px_0_#ffd400]">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#8a6f00]">
                  Виконано кроків
                </p>
                <p className="mt-2 text-4xl font-black text-black">
                  {backendProgress?.doneTasks ?? 0}
                  <span className="text-lg text-black/45">/{backendProgress?.totalTasks || 56}</span>
                </p>
              </div>
              <ClipboardCheck className="text-[#ffd400]" size={36} />
            </div>
          </div>

          <div className="rounded-[24px] border-2 border-black bg-white p-5 shadow-[4px_4px_0_#ffd400]">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#8a6f00]">
                  Завершені дні
                </p>
                <p className="mt-2 text-4xl font-black text-black">
                  {totalCompletedDays}
                  <span className="text-lg text-black/45">/28</span>
                </p>
              </div>
              <CalendarCheck2 className="text-[#ffd400]" size={36} />
            </div>
          </div>

          <div className="rounded-[24px] border-2 border-black bg-white p-5 shadow-[4px_4px_0_#ffd400]">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#8a6f00]">
                  Напрям
                </p>
                <p className="mt-2 line-clamp-2 text-2xl font-black text-black">{selectedCenter}</p>
              </div>
              <Target className="text-[#ffd400]" size={36} />
            </div>
          </div>
        </div>

        <section className="mt-6 overflow-hidden rounded-[28px] border-2 border-black bg-white p-4 shadow-[4px_4px_0_#ffd400] sm:p-6 lg:p-8">
          <div className="mb-8 grid gap-5 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <div>
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#8a6f00]">
                  30 днів програми
                </p>
                <h3 className="mt-1 text-3xl font-black uppercase tracking-[-0.04em] text-black">
                  Шлях до нової роботи
                </h3>
              </div>
              <p className="mt-3 max-w-[720px] text-sm font-semibold leading-6 text-[#5f6472]">
                Успіх рахується з виконаних днів і задач у твоєму маршруті.
                Коли команда відмічає активності, ця сторінка оновлюється для
                конкретного користувача.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <div className="inline-flex items-center gap-3 rounded-2xl bg-[#fff8cf] px-4 py-3">
                  <Users className="text-black" size={19} />
                  <div>
                    <p className="text-sm font-black leading-tight text-black">Спільнота МОЖУ</p>
                    <p className="text-xs font-semibold text-[#6b665d]">
                      Підтримка консультантів і групи
                    </p>
                  </div>
                </div>
                <div className="inline-flex items-center gap-3 rounded-2xl bg-[#f1f3f7] px-4 py-3">
                  <BookOpen className="text-black" size={19} />
                  <div>
                    <p className="text-sm font-black leading-tight text-black">Практичне навчання</p>
                    <p className="text-xs font-semibold text-[#6b665d]">
                      Кроки прив'язані до реальних задач
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 rounded-[24px] border-2 border-black bg-[#fffdf2] p-4 shadow-[3px_3px_0_#ffd400]">
              <div className="origin-left scale-[0.72] sm:scale-[0.78]">
                <CircularProgress value={overallPercent} />
              </div>
              <div className="-ml-8 min-w-[150px] sm:-ml-6">
                <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#8a6f00]">
                  Твій прогрес
                </p>
                <p className="mt-1 text-2xl font-black text-black">
                  {backendProgress?.doneTasks ?? 0}
                  <span className="text-sm text-black/45">/{backendProgress?.totalTasks || 56} кроків</span>
                </p>
                <p className="mt-1 text-xs font-semibold leading-5 text-[#5f6472]">
                  {totalCompletedDays} з 28 днів завершено
                </p>
              </div>
            </div>
          </div>

          {loading && (
            <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#f1f3f7] px-4 py-2 text-xs font-black text-[#5f6472]">
              <Loader2 size={15} className="animate-spin" />
              Завантаження
            </div>
          )}

          <div className="relative pb-2">
            <div className="absolute inset-0 rounded-[28px] bg-[radial-gradient(circle_at_10%_12%,rgba(255,212,0,0.14),transparent_28%),radial-gradient(circle_at_86%_34%,rgba(17,17,17,0.045),transparent_24%),radial-gradient(circle_at_18%_82%,rgba(255,212,0,0.12),transparent_28%)]" />
            <div className="absolute bottom-8 left-7 top-4 w-[8px] rounded-full border-2 border-black bg-[repeating-linear-gradient(180deg,#ffd400_0,#ffd400_18px,#111_18px,#111_24px)] lg:left-1/2 lg:-translate-x-1/2" />
            <div className="relative space-y-10 lg:space-y-12">
              {roadmap.map((step, index) => {
                const week = questWeeks.find((item) => item.week === step.week);
                const stats = getWeekStats(week);
                const isCurrent = currentStep.week === step.week && stats.percent < 100;
                const isDone = stats.percent >= 100;
                const isLocked = !week && step.week > (backendProgress?.weeksCount || 0) + 1;
                const Icon = step.icon;
                const isLeft = index % 2 === 0;
                const statusLabel = isDone ? "завершено" : isCurrent ? "у роботі" : isLocked ? "попереду" : "відкрито";
                const completedItems = Math.ceil((stats.percent / 100) * step.items.length);
                const Card = (
                  <div
                    className={cn(
                      "relative overflow-hidden rounded-[28px] border-2 p-4 transition duration-200 sm:p-5",
                      isCurrent
                        ? "border-black bg-[#fff8cf] shadow-[7px_7px_0_#000]"
                        : isDone
                        ? "border-black bg-white shadow-[5px_5px_0_#ffd400]"
                        : "border-black bg-white shadow-[4px_4px_0_rgba(255,212,0,0.75)]",
                      isLocked && "opacity-70"
                    )}
                  >
                    <div className="absolute right-0 top-0 h-full w-24 opacity-20">
                      <div className="h-full w-full bg-[repeating-linear-gradient(135deg,rgba(255,212,0,0.9)_0,rgba(255,212,0,0.9)_2px,transparent_2px,transparent_13px)]" />
                    </div>
                    <div className="absolute -bottom-10 -right-10 h-28 w-28 rounded-full bg-[#ffd400]/20" />

                    <div className="relative z-10">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-black px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[#ffd400]">
                            {step.label}
                          </span>
                          <span
                            className={cn(
                              "rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em]",
                              isDone
                                ? "bg-black text-[#ffd400]"
                                : isCurrent
                                ? "bg-[#ffd400] text-black"
                                : "bg-[#f1f3f7] text-[#6b7280]"
                            )}
                          >
                            {statusLabel}
                          </span>
                        </div>
                        <span className="rounded-full border border-black/10 bg-white/85 px-3 py-1 text-[11px] font-black text-black">
                          {stats.percent}%
                        </span>
                      </div>

                      <div className="mt-4 flex gap-3">
                        <div className="hidden h-12 w-12 shrink-0 place-items-center rounded-2xl border-2 border-black bg-[#ffd400] text-black sm:grid">
                          <Icon size={22} />
                        </div>
                        <div className="min-w-0">
                          <h4 className="text-2xl font-black uppercase tracking-[-0.04em] text-black">
                            {step.title}
                          </h4>
                          <p className="mt-2 text-sm font-semibold leading-6 text-[#5f6472]">
                            {step.subtitle}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        {step.items.map((item, itemIndex) => (
                          <span
                            key={item}
                            className={cn(
                              "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-black",
                              itemIndex < completedItems
                                ? "border-black bg-[#ffd400] text-black"
                                : "border-black/10 bg-white text-[#5f6472]"
                            )}
                          >
                            {itemIndex < completedItems ? (
                              <CheckCircle2 size={13} />
                            ) : (
                              <Circle size={13} />
                            )}
                            {item}
                          </span>
                        ))}
                      </div>

                      <div className="mt-5 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                        <div>
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-xs font-black uppercase tracking-[0.1em] text-[#8a6f00]">
                              Прогрес етапу
                            </p>
                            <p className="text-xs font-black text-black">
                              {stats.done}/{stats.total}
                            </p>
                          </div>
                          <div className="mt-2 h-3 overflow-hidden rounded-full border border-black/10 bg-[#f1e7b8]">
                            <div
                              className="h-full rounded-full bg-[#ffd400]"
                              style={{ width: `${stats.percent}%` }}
                            />
                          </div>
                        </div>
                        <div className="rounded-2xl border border-black/10 bg-white px-4 py-3">
                          <p className="min-w-[64px] text-center text-3xl font-black leading-none text-black">
                            {stats.percent}%
                          </p>
                        </div>
                      </div>

                      <p className="mt-4 rounded-2xl bg-[#f7f8fb] px-4 py-3 text-xs font-bold leading-5 text-[#5f6472] lg:hidden">
                        {step.result}
                      </p>
                    </div>
                  </div>
                );
                const Result = (
                  <div
                    className={cn(
                      "hidden lg:block",
                      isLeft ? "pl-3" : "pr-3"
                    )}
                  >
                    <div
                      className={cn(
                        "relative rounded-[24px] border-2 border-black/10 bg-[#fffdf2] p-5 shadow-[3px_3px_0_rgba(0,0,0,0.08)]",
                        isLeft ? "text-left" : "text-right"
                      )}
                    >
                      <p className="text-[11px] font-black uppercase tracking-[0.12em] text-[#8a6f00]">
                        Фініш етапу
                      </p>
                      <p className="mt-2 text-sm font-bold leading-6 text-[#5f6472]">
                        {step.result}
                      </p>
                    </div>
                  </div>
                );

                return (
                  <article
                    key={step.week}
                    className="relative grid grid-cols-[64px_minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_88px_minmax(0,1fr)] lg:items-center lg:gap-6"
                  >
                    <div className="hidden lg:col-start-1 lg:block">
                      {isLeft ? Card : Result}
                    </div>

                    <div className="relative z-10 col-start-1 row-start-1 flex justify-center lg:col-start-2">
                      <div
                        className={cn(
                          "relative grid h-16 w-16 place-items-center rounded-full border-2 border-black shadow-[4px_4px_0_#000]",
                          isDone
                            ? "bg-black text-[#ffd400]"
                            : isCurrent
                            ? "bg-[#ffd400] text-black"
                            : "bg-white text-black"
                        )}
                      >
                        {isDone ? <CheckCircle2 size={28} /> : <Icon size={28} />}
                        <span
                          className={cn(
                            "absolute -bottom-3 left-1/2 grid h-7 w-7 -translate-x-1/2 place-items-center rounded-full border-2 border-black text-[11px] font-black",
                            isDone ? "bg-[#ffd400] text-black" : "bg-black text-[#ffd400]"
                          )}
                        >
                          {step.week}
                        </span>
                      </div>
                    </div>

                    <div className="col-start-2 row-start-1 lg:hidden">
                      {Card}
                    </div>

                    <div className="hidden lg:col-start-3 lg:block">
                      {isLeft ? Result : Card}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      </section>
    </main>
  );
};

export default YellowQuestCabinetPage;
